import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentsNsrAggregatesIO } from '../types';

export const getGetAppointmentsNsrAggregatesQueryKey = (req: GetAppointmentsNsrAggregatesIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentsNsrAggregates', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentsNsrAggregatesQueryKey>;

/**
 * A hook that returns a query for the `GetAppointmentsNsrAggregates` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsNsrAggregatesQuery = <E = unknown, D = GetAppointmentsNsrAggregatesIO['output']>(
  req: GetAppointmentsNsrAggregatesIO['input'],
  options?: LimitedSchemaQueryOptions<GetAppointmentsNsrAggregatesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetAppointmentsNsrAggregatesIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentsNsrAggregatesQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrAggregates(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetAppointmentsNsrAggregates` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsNsrAggregatesQueries = <E = unknown, D = GetAppointmentsNsrAggregatesIO['output']>(
  argsArr: {
    req: GetAppointmentsNsrAggregatesIO['input'];
    options?: LimitedSchemaQueryOptions<GetAppointmentsNsrAggregatesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetAppointmentsNsrAggregatesQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrAggregates(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetAppointmentsNsrAggregatesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentsNsrAggregates` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentsNsrAggregates = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentsNsrAggregates = async <E = unknown, D = GetAppointmentsNsrAggregatesIO['output']>(
    req: GetAppointmentsNsrAggregatesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetAppointmentsNsrAggregatesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetAppointmentsNsrAggregatesIO['output'], E, D, QueryKey>(
      getGetAppointmentsNsrAggregatesQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrAggregates(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentsNsrAggregates;
};
